import { Meal } from "@/clients/models/Meal";
import { MealTime } from "@/clients/models/MealTime";
import { HeartFilledIcon } from "@/components/icons/heart-filled-icon";
import { HeartIcon } from "@/components/icons/heart-icon";
import { TextButton } from "@/components/ui/buttons";
import { cn } from "@/utils/cn";
import { memo } from "react";
import { useAddFavouriteMeal } from "../../hooks/use-add-favourite-meal";
import { useGetFavouriteMeals } from "../../hooks/use-get-favourite-meals";
import { useRemoveFavouriteMeal } from "../../hooks/use-remove-favourite-meal";

interface LikeMealButtonProps {
  meal: Meal;
  mealTime: MealTime | undefined;
  /** Optional request ID from alternatives session; included when favouriting from swap view */
  requestId?: string | null;
  className?: string;
  ariaLabel?: string;
  filledColor?: "black" | "red" | "primary";
  unfilledColor?: "black" | "red" | "primary";
}

export const LikeMealButton = memo(function LikeMealButton({
  meal,
  mealTime,
  requestId,
  className,
  ariaLabel = "Like this meal",
  filledColor = "black",
  unfilledColor = "black",
}: LikeMealButtonProps) {
  const { favouriteMealsSet, isFavouriteLoading } = useGetFavouriteMeals();
  const currentMealId = meal?.id ?? "";
  const isLiked = currentMealId ? favouriteMealsSet.has(currentMealId) : false;
  const { addFavouriteMeal, isAddingFavouriteMeal } = useAddFavouriteMeal();
  const { removeFavouriteMeal, isRemovingFavouriteMeal } = useRemoveFavouriteMeal();

  const handleClick = () => {
    if (!meal?.id || !mealTime) {
      return;
    }
    if (isLiked) {
      removeFavouriteMeal({
        meal_id: meal.id,
        meal_time: mealTime,
      });
      return;
    }

    if (isAddingFavouriteMeal) {
      return;
    }

    console.log("requestId", requestId);

    addFavouriteMeal({
      meal_id: meal.id,
      meal_time: mealTime,
      ...(requestId != null && requestId !== "" && { requestId }),
    });
  };

  return (
    <TextButton
      type="button"
      aria-label={ariaLabel}
      onClick={handleClick}
      color="black"
      disabled={isAddingFavouriteMeal || isRemovingFavouriteMeal || isFavouriteLoading}
      className={cn("flex h-14 w-14 items-center justify-center rounded-full", className)}
    >
      {isLiked ? (
        <HeartFilledIcon
          className={cn({
            "fill-black": filledColor === "black",
            "fill-[#EB190A]": filledColor === "red",
            "fill-os-brand": filledColor === "primary",
          })}
        />
      ) : (
        <HeartIcon
          className={cn("fill-black", {
            "fill-black": unfilledColor === "black",
            "fill-[#EB190A]": unfilledColor === "red",
            "fill-os-brand": unfilledColor === "primary",
          })}
        />
      )}
    </TextButton>
  );
});
